import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useCallback, useEffect, useRef } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router-dom';

export interface UseUnsavedChangesWarningOptions {
  when: boolean;
  title?: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * Asks before leaving while `when` holds: a confirm dialog for in-app navigation,
 * the browser's own prompt for tab close and reload. Call the returned function
 * right before a navigation that should pass, such as after a save.
 */
export function useUnsavedChangesWarning({
  when,
  title = 'Leave without saving?',
  body = 'Your changes will be lost.',
  confirmLabel = 'Leave',
  cancelLabel = 'Stay',
}: UseUnsavedChangesWarningOptions): () => void {
  const allowRef = useRef(false);

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        when &&
        !allowRef.current &&
        (currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search),
      [when]
    )
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let decided = false;
    modals.openConfirmModal({
      title,
      children: <Text size="sm">{body}</Text>,
      labels: { confirm: confirmLabel, cancel: cancelLabel },
      confirmProps: { variant: 'danger' },
      onConfirm: () => {
        decided = true;
        blocker.proceed();
      },
      onCancel: () => {
        decided = true;
        blocker.reset();
      },
      onClose: () => {
        if (!decided) blocker.reset();
      },
    });
  }, [blocker, title, body, confirmLabel, cancelLabel]);

  useEffect(() => {
    if (!when) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  return useCallback(() => {
    allowRef.current = true;
  }, []);
}
